import { useRef, useState, type ReactNode } from 'react';
import { Divider } from 'antd';
import type { Editor } from '@tiptap/react';
import { MentionIcon, URLIcon } from '@/assets/icons/icons';
import { AddLinkForm } from '@/pages/Shipments/partials';
import {
  getEditorSelectedText,
  insertEditorLink,
} from '@/pages/Shipments/partials/editorLinks';
import styles from './TextTemplateInput.module.css';

interface RichTextToolbarProps {
  editor: Editor;
  /** Shown first in the toolbar, followed by a divider. */
  leadingContent?: ReactNode;
}

/**
 * The row under a rich-text field: `leadingContent`, then a button that
 * starts an @ mention and one that adds a link.
 */
export function RichTextToolbar({
  editor,
  leadingContent,
}: RichTextToolbarProps) {
  const [linkFormVisible, setLinkFormVisible] = useState(false);
  const [linkText, setLinkText] = useState('');

  // What `AddLinkForm` reads the selection from and inserts the link through.
  const linkTarget = useRef({
    getSelectedText: () => getEditorSelectedText(editor),
    insertLink: (link: { text?: string; href: string }) =>
      insertEditorLink(editor, link),
  });

  /** Opens the Add Link form with the selected text as the link's text. */
  const openLinkForm = () => {
    setLinkText(getEditorSelectedText(editor));
    setLinkFormVisible(true);
  };

  return (
    <div className={styles.richTextToolbar}>
      {leadingContent !== undefined && (
        <>
          {leadingContent}
          <Divider type='vertical' style={{ height: '20px' }} />
        </>
      )}
      <button
        type='button'
        className={styles.toolbarButton}
        aria-label='Mention someone'
        onClick={() => editor.chain().focus().insertContent('@').run()}
      >
        <MentionIcon />
      </button>
      <button
        type='button'
        className={styles.toolbarButton}
        aria-label='Add link'
        onClick={openLinkForm}
      >
        <URLIcon />
      </button>
      <AddLinkForm
        editorRef={linkTarget}
        setVisible={setLinkFormVisible}
        visible={linkFormVisible}
        setLinkText={setLinkText}
        linkText={linkText}
      />
    </div>
  );
}
